import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormControl} from '@angular/forms';
import {Subject, Subscription} from 'rxjs';
import {MatSort, Sort} from '@angular/material/sort';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {TableControl} from '../../../../common/models/TableControl';
import {IssueTicketDTO} from '../../../../clm/models/IssueTicketDTO';
import {DateUtil} from '../../../../common/util/DateUtil';
import {ActivatedRoute, Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {IssueTicketService} from '../../../services/issue-ticket.service';
import {debounceTime} from 'rxjs/operators';
import {PagedRequestDTO} from '../../../../common/models/PagedRequestDTO';
import {Common, getSortDirection} from '../../../../common/util/Util';
import {ErrorDialogComponent} from '../../../../common/dialogs/error-dialog/error-dialog.component';
import {ConfirmationDialogComponent} from '../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {AddIssueTicketDialogComponent} from '../../../../clm/components/dialogs/add-issue-ticket-dialog/add-issue-ticket-dialog.component';
import {NameValueDTO} from '../../../../common/models/NameValueDTO';
import {LookupService} from '../../../../common/services/lookup.service';
import {PagedFilterDTO} from '../../../../common/models/PagedFilterDTO';
import {KeycloakService} from 'keycloak-angular';
import {detailsExpand} from '../../../../common';
import {EntityTable} from '../../../../common/types/entity-table.enum';
import {LookupValue} from '../../../../clm/types/lookup-value.enum';

enum TicketSortFilterValue {
  ASSIGNEE_FIRST_NAME = 'assigneeByUserId.firstName',
  ASSIGNEE_LAST_NAME = 'assigneeByUserId.lastName',
  REPORTER = 'reporterByUserId.lastName',
  MODULE = 'moduleByModuleId.name',
  PRIORITY = 'priorityByPriorityId.name',
  STATUS = 'statusByStatusId.name',
  RESOLUTION = 'resolutionByResolutionId.name'
}
const ALL_STATUSES = 'All Statuses';
const ALL_ASSIGNEES = 'All Users';
const OPEN_STATUS = 'Open';

@Component({
  selector: 'app-issue-ticket-management-view',
  templateUrl: './issue-ticket-management-view.component.html',
  styleUrls: [
    './issue-ticket-management-view.component.scss',
    '../../../../../../styles/global/_management-styles.scss'
  ],
  animations: [detailsExpand]
})
export class IssueTicketManagementViewComponent implements OnInit, OnDestroy {
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly ticketSortFilterValue = TicketSortFilterValue;
  readonly dateFormat = DateUtil.dateFormat;
  readonly dateTimeFormat = DateUtil.dateTimeFormat;
  readonly lookupValue = LookupValue;

  @ViewChild('ticketSort', { static: false }) ticketSort: MatSort;
  @ViewChild('ticketPaginator', { static: false }) ticketPaginator: MatPaginator;
  @ViewChild('ticketSearch') ticketSearch: ElementRef;

  ticketNameFormControl: FormControl;
  ticketStatusFormControl: FormControl;
  ticketAssigneeFormControl: FormControl;
  ticketNameSubscription: Subscription;
  ticketNameTextChanged = new Subject<string>();
  statuses: NameValueDTO[];
  assignees: NameValueDTO[];
  ticketDataSource: IssueTicketDTO[];
  ticketTableControl: TableControl;
  ticketExpandedRow: IssueTicketDTO | null;
  ticketDisplayedColumns: string[] = [
    'icons',
    'name',
    'assignee',
    'reporter',
    'module',
    'priority',
    'startDate',
    'endDate',
    'dueDate',
    'status',
    'resolution'
  ];
  length = 100;
  pageSize = 10;
  pageNumber = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];
  isLoading = false;
  userFirstName;
  userLastName;

  constructor(
    private ticketService: IssueTicketService,
    private lookupService: LookupService,
    private keycloakService: KeycloakService,
    private snackBar: MatSnackBar,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private formBuilder: FormBuilder
  ) {
    this.userFirstName = '';
    this.userLastName = '';

    this.ticketNameFormControl = new FormControl('');
    this.ticketAssigneeFormControl = new FormControl(ALL_ASSIGNEES);
    this.ticketStatusFormControl = new FormControl(OPEN_STATUS);

    this.ticketNameSubscription = this.ticketNameTextChanged.pipe(
      debounceTime(this.debounceTime)
    ).subscribe(() => {
      this.filterTickets();
    });
  }

  ngOnInit(): void {
    this.keycloakService.loadUserProfile().then((profile) => {
      this.userFirstName = profile.firstName;
      this.userLastName = profile.lastName;
    });
    this.ticketTableControl = new TableControl(
      this.length,
      this.pageSize,
      this.pageNumber,
      this.pageSizeOptions,
      'startDate',
      'desc',
      'name',
      ''
    );
    this.getListOfLookupValues(this.lookupValue.IssueStatus);
    this.getListOfLookupValues(this.lookupValue.User); // filterTickets() called from _setAssigneeIfExistsAndHasRecords within this method
  }

  ngOnDestroy(): void {
    this.ticketNameSubscription.unsubscribe();
  }

  onInputChangedSearchTicketsByName($event) {
    if (this.ticketPaginator != null) {
      this.ticketPaginator.firstPage();
    }
    this.ticketTableControl.filterField = 'name';
    this.ticketTableControl.filterValue = $event.target.value;
    this.ticketNameTextChanged.next($event.target.value);
  }

  searchForTicketsByStatusOrManager() {
    if (this.ticketPaginator != null) {
      this.ticketPaginator.firstPage();
    }
    this.filterTickets();
  }

  filterTickets() {
    this.getListOfTickets(this.ticketTableControl, this.ticketStatusFormControl.value, this.ticketAssigneeFormControl.value);
  }

  sortData($event: Sort) {
    if (!$event.direction) {
      return;
    }
    if (this.ticketPaginator != null) {
      this.ticketPaginator.firstPage();
    }

    if ($event.active === 'assignee') {
      this.ticketTableControl.sortField = TicketSortFilterValue.ASSIGNEE_LAST_NAME;
    }
    else if ($event.active === 'reporter') {
      this.ticketTableControl.sortField = TicketSortFilterValue.REPORTER;
    }
    else if ($event.active === 'module') {
      this.ticketTableControl.sortField = TicketSortFilterValue.MODULE;
    }
    else if ($event.active === 'priority') {
      this.ticketTableControl.sortField = TicketSortFilterValue.PRIORITY;
    }
    else if ($event.active === 'status') {
      this.ticketTableControl.sortField = TicketSortFilterValue.STATUS;
    }
    else if ($event.active === 'resolution') {
      this.ticketTableControl.sortField = TicketSortFilterValue.RESOLUTION;
    }
    else {
      this.ticketTableControl.sortField = $event.active;
    }

    this.ticketTableControl.pageNumber = 0;
    this.ticketTableControl.sortDirection = $event.direction;
    this.filterTickets();
  }
  pageChanged($event: PageEvent) {
    this.ticketTableControl.pageNumber = $event.pageIndex;
    this.ticketTableControl.pageSize = $event.pageSize;
    this.filterTickets();
  }

  deleteEntry(event: MouseEvent, id: number) {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: 'Are you sure you wish to delete this entity?',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.isLoading = true;
        this.ticketService.deleteIssueTicket(id).subscribe(
          () => {
            this.openSnackBar(`Ticket ${id} deleted`, 'Dismiss');
            this.filterTickets();
            this.isLoading = false;
          },
          (errorResponse) => {
            const error = errorResponse.error;
            this.openErrorDialog(error.businessMessage);
            this.isLoading = false;
          }
        );
      }
    });
  }

  editEntry(event: MouseEvent, id: number) {
    event.preventDefault();
    event.stopPropagation();

    let selectedIssueTicketCase: IssueTicketDTO;

    this.isLoading = true;
    this.ticketService.getIssueTicket(id).subscribe(
      (response) => {
        this.isLoading = false;
        selectedIssueTicketCase = response;
        this.openTicketDialog(selectedIssueTicketCase);
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.openErrorDialog(error.businessMessage);
        this.isLoading = false;
      }
    );

  }

  openTicketDialog(ticket?: IssueTicketDTO) {
    const dialogRef = this.dialog.open(AddIssueTicketDialogComponent, {
      width: '60vw',
      height: '85vh',
      data: {
        isEditMode: !!ticket,
        issueTicketCase: ticket,
        formGroup: this.formBuilder.group({})
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        const {
          issueTicketCasePayload,
          formGroup
        } = result;

        if (issueTicketCasePayload) {
          this.isLoading = true;
          // TODO: make AddIssueTicketDialog generic to avoid use of issueTicketCase
          this.ticketService.saveIssueTicket(issueTicketCasePayload.issueTicketDTO).subscribe(
            (response) => {
              this.isLoading = false;
              this.openSnackBar(
                `Ticket ${response.name} saved successfully`,
                'Dismiss'
              );
              this.filterTickets();
            },
            (errorResponse) => {
              const error = errorResponse.error;
              this.openErrorDialog(error.businessMessage);
              this.isLoading = false;
            }
          );
        }
      }
    });
  }

  private getListOfTickets(tableControl: TableControl, status?: string, assignee?: string): void {
    this.isLoading = true;

    let firstName = '';
    let lastName = '';
    if (assignee != null) {
    lastName = assignee.split(' ')[assignee.split(' ').length - 1];
    firstName = assignee.replace(` ${lastName}`, '');
  }

    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblIssueTicket;
    pagedRequestDTO.pageNumber = tableControl.pageNumber;
    pagedRequestDTO.pageSize = tableControl.pageSize;
    pagedRequestDTO.sortField = tableControl.sortField;
    pagedRequestDTO.sortDirection = getSortDirection(
      tableControl.sortDirection
    );

    const generalPagedFilterDTO = new PagedFilterDTO();
    generalPagedFilterDTO.name = tableControl.filterField;
    generalPagedFilterDTO.value = tableControl.filterValue;

    const statusPagedFilterDTO = new PagedFilterDTO();
    statusPagedFilterDTO.name = TicketSortFilterValue.STATUS;
    statusPagedFilterDTO.value = status;

    const assigneeFirstNamePagedFilterDTO = new PagedFilterDTO();
    assigneeFirstNamePagedFilterDTO.name = TicketSortFilterValue.ASSIGNEE_FIRST_NAME;
    assigneeFirstNamePagedFilterDTO.value = firstName;

    const assigneeLastNamePagedFilterDTO = new PagedFilterDTO();
    assigneeLastNamePagedFilterDTO.name = TicketSortFilterValue.ASSIGNEE_LAST_NAME;
    assigneeLastNamePagedFilterDTO.value = lastName;

    pagedRequestDTO.fieldMap = {};
    pagedRequestDTO.fieldMap[tableControl.filterField] = generalPagedFilterDTO;
    if (status != null && status !== '' && status !== ALL_STATUSES) {
      pagedRequestDTO.fieldMap[TicketSortFilterValue.STATUS.split('.')[0]] = statusPagedFilterDTO;
    }
    if (assignee != null && assignee !== '' && assignee !== ALL_ASSIGNEES) {
      pagedRequestDTO.fieldMap[TicketSortFilterValue.ASSIGNEE_FIRST_NAME.split('.')[0] + '_1'] = assigneeFirstNamePagedFilterDTO;
      pagedRequestDTO.fieldMap[TicketSortFilterValue.ASSIGNEE_LAST_NAME.split('.')[0] + '_2'] = assigneeLastNamePagedFilterDTO;
    }

    this.ticketService.getListOfIssueTickets(pagedRequestDTO).subscribe(
      (response) => {
        this.isLoading = false;
        this.ticketDataSource = response.resultList;
        this.ticketTableControl.length = response.totalRecords;
      },
      (errorResponse) => {
        this.isLoading = false;
        const error = errorResponse.error;
        this.openErrorDialog(error.businessMessage);
      }
    );
  }

  private getListOfLookupValues(entity: string): void {
    this.lookupService.getListOfLookupValues(entity).subscribe(
      (response: NameValueDTO[]) => {
        if (entity === this.lookupValue.IssueStatus) {
          this.statuses = response;
          this.statuses.unshift({value: 0, name: ALL_STATUSES});
        } else if (entity === this.lookupValue.User) {
          this.assignees = response;
          this.assignees.unshift({value: 0, name: ALL_ASSIGNEES});
          this.setAssigneeIfExistsAndHasRecords();
        }
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.openErrorDialog(error.businessMessage);
      }
    );
  }

  private setAssigneeIfExistsAndHasRecords() {
    const foundAssignee = this.assignees.find(assignee => {
      return assignee.name === `${this.userFirstName} ${this.userLastName}`;
    });
    if (foundAssignee != null) {
      const pagedRequestDTO = this.buildTotalRecordCountRequestDTO();

      this.ticketService.getListOfIssueTickets(pagedRequestDTO).subscribe(
        (response) => {
          if (response.totalRecords > 0) {
            this.ticketAssigneeFormControl.setValue(foundAssignee.name);
            this.filterTickets();
          }
        }
      );
    }
    this.filterTickets();
  }

  private buildTotalRecordCountRequestDTO() {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblIssueTicket;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = 1;

    const statusPagedFilterDTO = new PagedFilterDTO();
    statusPagedFilterDTO.name = TicketSortFilterValue.STATUS;
    statusPagedFilterDTO.value = OPEN_STATUS;

    const assigneeFirstNamePagedFilterDTO = new PagedFilterDTO();
    assigneeFirstNamePagedFilterDTO.name = TicketSortFilterValue.ASSIGNEE_FIRST_NAME;
    assigneeFirstNamePagedFilterDTO.value = this.userFirstName;

    const assigneeLastNamePagedFilterDTO = new PagedFilterDTO();
    assigneeLastNamePagedFilterDTO.name = TicketSortFilterValue.ASSIGNEE_LAST_NAME;
    assigneeLastNamePagedFilterDTO.value = this.userLastName;

    pagedRequestDTO.fieldMap = {};
    pagedRequestDTO.fieldMap[TicketSortFilterValue.STATUS.split('.')[0]] = statusPagedFilterDTO;
    pagedRequestDTO.fieldMap[TicketSortFilterValue.ASSIGNEE_FIRST_NAME.split('.')[0] + '_1'] = assigneeFirstNamePagedFilterDTO;
    pagedRequestDTO.fieldMap[TicketSortFilterValue.ASSIGNEE_LAST_NAME.split('.')[0] + '_2'] = assigneeLastNamePagedFilterDTO;
    return pagedRequestDTO;
  }

  private openErrorDialog(errorMessage: string) {
    this.dialog.open(ErrorDialogComponent, {
      width: '40vw',
      data: errorMessage,
    });
  }

  private openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, {
      duration: 10000,
    });
  }
}
